import { motion } from "framer-motion";
import WhatsAppWhite from "../../assets/icon/WhatsAppWhite.svg";
import ProductGallery from "./ProductGallery";
import ProductSpecsTable from "./ProductSpecsTable";

interface ProductCategory {
  id: number;
  name: string;
}

interface ProductImage {
  id: number;
  image: string;
}

interface ProductDescription {
  id: number;
  type: string;
  standardMaterial: string;
  pressure: string;
  temperature: string;
  surfaceSpeed: string;
  stock: number;
  unlimited: boolean;
}

interface ProductDetailResponse {
  id: number;
  name: string;
  slug: string;
  category: ProductCategory;
  subCategory: ProductCategory;
  status: boolean;
  createdAt?: string;
  updatedAt?: string;
  productImages: ProductImage[];
  productDemoImages: ProductImage[];
  descriptions: ProductDescription[];
}

interface ProductInfoCardProps {
  product: ProductDetailResponse;
}

// WAJIB diganti sesuai nomor WhatsApp perusahaan yang asli.
const WHATSAPP_NUMBER = "628123456789";

export default function ProductInfoCard({ product }: ProductInfoCardProps) {
  const totalStock = product.descriptions.reduce(
    (sum, d) => sum + (d.unlimited ? 0 : d.stock),
    0
  );
  const hasUnlimited = product.descriptions.some((d) => d.unlimited);
  const inStock = hasUnlimited || totalStock > 0;

  const waMessage = encodeURIComponent(
    `Halo, saya tertarik dengan produk "${product.name}". Apakah masih tersedia?`
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-[32px] bg-white p-6 shadow-xl shadow-slate-200/60 lg:p-10">
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
        {/* GALLERY */}
        <ProductGallery images={product.productImages} productName={product.name} />

        {/* INFO */}
        <div className="flex flex-col">
          {/* Badges kategori */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#D8EEF0] px-4 py-1.5 text-xs font-semibold text-[#2C4858]">
              {product.category.name}
            </span>
            <span className="rounded-full bg-slate-100 px-4 py-1.5 text-xs font-medium text-slate-500">
              {product.subCategory.name}
            </span>
          </div>

          {/* Nama produk */}
          <h1 className="mt-4 text-3xl font-bold leading-tight text-[#324A5A] lg:text-4xl">
            {product.name}
          </h1>

          {/* Stock indicator */}
          <div className="mt-3 flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                inStock ? "bg-emerald-500" : "bg-red-400"
              }`}
            />
            <span className="text-sm font-medium text-slate-500">
              {hasUnlimited
                ? "Stock tersedia"
                : inStock
                ? `${totalStock} pcs tersedia`
                : "Stock habis"}
            </span>
          </div>

          <div className="my-7 h-px w-full bg-slate-100" />

          {/* Deskripsi singkat - varian pertama sebagai ringkasan */}
          {product.descriptions[0] && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <SummaryItem label="Type" value={product.descriptions[0].type} />
              <SummaryItem
                label="Material"
                value={product.descriptions[0].standardMaterial}
              />
              <SummaryItem label="Pressure" value={product.descriptions[0].pressure} />
              <SummaryItem
                label="Temperature"
                value={product.descriptions[0].temperature}
              />
            </div>
          )}

          {/* CTA */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMessage}`}
            target="_blank"
            rel="noreferrer"
            className="
              mt-8
              inline-flex
              w-full
              items-center
              justify-center
              gap-3

              rounded-2xl

              bg-[#11C900]

              px-8
              py-4

              text-lg
              font-semibold
              text-white

              shadow-lg
              shadow-green-500/20

              transition-all
              duration-200

              hover:-translate-y-0.5
              hover:bg-[#0fb100]
              hover:shadow-xl

              lg:w-auto
            ">
            Pesan Sekarang
            <img src={WhatsAppWhite} alt="" className="h-6 w-6" />
          </a>
        </div>
      </div>

      {/* SPESIFIKASI LENGKAP (semua varian) */}
      {product.descriptions.length > 0 && (
        <div className="mt-12">
          <h3 className="mb-5 text-xl font-bold text-[#324A5A]">
            Spesifikasi Produk
            {product.descriptions.length > 1 && (
              <span className="ml-2 text-sm font-normal text-slate-400">
                ({product.descriptions.length} varian tersedia)
              </span>
            )}
          </h3>
          <ProductSpecsTable descriptions={product.descriptions} />
        </div>
      )}
    </motion.div>
  );
}

function SummaryItem({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="mt-0.5 font-medium text-[#324A5A]">{value ?? "-"}</p>
    </div>
  );
}
